import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { getUser, setAuth } from '@/lib/auth';
import api from '@/lib/api';

export default function ForceChangePasswordPage() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = getUser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) { toast.error('Please fill all fields'); return; }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    if (currentPw === newPw) { toast.error('New password must be different from current password'); return; }

    setLoading(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: currentPw,
        new_password: newPw,
      });
      // Update local storage to remove the force flag
      const updatedUser = { ...user, must_change_password: false };
      setAuth(localStorage.getItem('prismoline_token'), updatedUser);
      toast.success('Password changed successfully! Welcome aboard.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7FAFF] p-4" data-testid="force-change-password-page">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img src="/prismoline-logo.png" alt="Prismoline" className="h-10 mx-auto" />
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="pb-4 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF8DD] flex items-center justify-center mb-3">
              <ShieldCheck className="w-7 h-7 text-[#D4A800]" />
            </div>
            <h2 className="text-xl font-semibold text-[#0B1B33]">Set Your New Password</h2>
            <p className="text-sm text-slate-500 mt-1">
              Welcome, <strong>{user?.name}</strong>! For security, please change your default password before continuing.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Current Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPw}
                    onChange={e => setCurrentPw(e.target.value)}
                    placeholder="Enter current password"
                    className="pl-10 pr-10 h-11"
                    data-testid="force-current-password"
                  />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    placeholder="Choose a new password (min 6 chars)"
                    className="pl-10 pr-10 h-11"
                    data-testid="force-new-password"
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)}
                    placeholder="Re-enter new password"
                    className="pl-10 h-11"
                    data-testid="force-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#0D2850] hover:bg-[#0B2246] text-white font-semibold"
                data-testid="force-change-submit"
              >
                {loading ? 'Updating...' : 'Set New Password & Continue'}
              </Button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-4">
              You must change your password before accessing the system
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
